import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  Building2,
  UserRound,
  Save,
  Trash2,
  Download,
  Send,
  AlertTriangle,
  CheckCircle2,
  Target,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  apolloStatus,
  searchApolloCompanies,
  searchApolloPeople,
  saveApolloLeads,
  addManualLead,
  listApolloLeads,
  updateApolloLead,
  deleteApolloLead,
  pushLeadToHubspot,
} from "@/lib/api/apollo.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/prospecting")({
  component: ProspectingPage,
});

type Prospect = {
  apollo_id: string | null;
  kind: "company" | "person";
  name: string;
  title: string | null;
  company: string | null;
  domain: string | null;
  website_url: string | null;
  industry: string | null;
  location: string | null;
  email: string | null;
  phone: string | null;
  linkedin_url: string | null;
  employees: number | null;
};

const STATUSES = [
  "new",
  "contacted",
  "replied",
  "meeting",
  "client",
  "not_a_fit",
] as const;

const PLAYBOOKS: {
  label: string;
  keywords: string;
  titles: string;
  industries: string;
}[] = [
  {
    label: "SME employers (labour & contracts)",
    keywords: "",
    titles: "Human Resources Manager, HR Officer, Operations Manager, Owner",
    industries: "construction, retail, logistics, manufacturing, hospitality",
  },
  {
    label: "Compliance & POPIA buyers",
    keywords: "compliance",
    titles: "Compliance Officer, Information Officer, Finance Manager",
    industries: "financial services, healthcare, education, insurance",
  },
  {
    label: "Law firms (candidate attorney & support work)",
    keywords: "attorneys",
    titles: "Managing Partner, Practice Manager, Director",
    industries: "law practice, legal services",
  },
  {
    label: "Universities & training (LexPractiq)",
    keywords: "university",
    titles: "Dean, Head of Department, Career Services Manager",
    industries: "higher education, education management",
  },
];

function ProspectingPage() {
  const statusFn = useServerFn(apolloStatus);
  const companiesFn = useServerFn(searchApolloCompanies);
  const peopleFn = useServerFn(searchApolloPeople);
  const saveFn = useServerFn(saveApolloLeads);
  const manualFn = useServerFn(addManualLead);
  const listFn = useServerFn(listApolloLeads);
  const updateFn = useServerFn(updateApolloLead);
  const deleteFn = useServerFn(deleteApolloLead);
  const hubspotFn = useServerFn(pushLeadToHubspot);
  const qc = useQueryClient();

  const [mode, setMode] = useState<"company" | "person">("company");
  const [form, setForm] = useState({
    keywords: "",
    locations: "South Africa",
    industries: "",
    titles: "",
    domains: "",
    employeeRanges: "",
    page: 1,
    perPage: 25,
  });
  const [results, setResults] = useState<Prospect[]>([]);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [searchError, setSearchError] = useState<{ error: string; hint?: string } | null>(null);
  const [manual, setManual] = useState({ name: "", company: "", email: "", phone: "", notes: "" });

  const conn = useQuery({ queryKey: ["apollo-status"], queryFn: () => statusFn() });
  const leads = useQuery({ queryKey: ["apollo-leads"], queryFn: () => listFn() });

  const search = useMutation({
    mutationFn: async () => {
      const fn = mode === "company" ? companiesFn : peopleFn;
      return fn({ data: form });
    },
    onSuccess: (res: any) => {
      if (!res.ok) {
        setResults([]);
        setSearchError({ error: res.error, hint: res.hint });
        return;
      }
      setSearchError(null);
      setResults(res.results as Prospect[]);
      setPicked({});
      toast.success(`${res.results.length} results (${res.total ?? res.results.length} matches)`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveSelected = useMutation({
    mutationFn: async () => {
      const chosen = results.filter((r, i) => picked[r.apollo_id ?? String(i)]);
      if (!chosen.length) throw new Error("Select at least one prospect");
      return saveFn({ data: { leads: chosen, sourceQuery: { mode, ...form } } });
    },
    onSuccess: (r: any) => {
      toast.success(`Saved ${r.saved} lead(s) to the pipeline`);
      qc.invalidateQueries({ queryKey: ["apollo-leads"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const addManual = useMutation({
    mutationFn: async () => {
      if (!manual.name.trim()) throw new Error("Name is required");
      return manualFn({
        data: {
          kind: manual.company && manual.name !== manual.company ? "person" : "company",
          name: manual.name.trim(),
          company: manual.company.trim() || null,
          email: manual.email.trim() || null,
          phone: manual.phone.trim() || null,
        },
      });
    },
    onSuccess: () => {
      setManual({ name: "", company: "", email: "", phone: "", notes: "" });
      toast.success("Lead added");
      qc.invalidateQueries({ queryKey: ["apollo-leads"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const update = useMutation({
    mutationFn: (v: any) => updateFn({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apollo-leads"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Lead removed");
      qc.invalidateQueries({ queryKey: ["apollo-leads"] });
    },
  });

  const toHubspot = useMutation({
    mutationFn: (id: string) => hubspotFn({ data: { id } }),
    onSuccess: (r: any) => {
      if (r.ok) toast.success("Pushed to HubSpot");
      else toast.error(r.error);
      qc.invalidateQueries({ queryKey: ["apollo-leads"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rows = (leads.data?.leads ?? []) as any[];
  const stats = useMemo(() => {
    const by = (s: string) => rows.filter((r) => r.status === s).length;
    return {
      total: rows.length,
      contacted: by("contacted") + by("replied") + by("meeting") + by("client"),
      clients: by("client"),
      hubspot: rows.filter((r) => r.synced_to_hubspot).length,
    };
  }, [rows]);

  function exportCsv() {
    const header = [
      "name",
      "title",
      "company",
      "email",
      "phone",
      "industry",
      "location",
      "linkedin_url",
      "status",
    ];
    const csv = [
      header.join(","),
      ...rows.map((r) =>
        header
          .map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`)
          .join(","),
      ),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `ndl-prospects-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <h1 className="flex items-center gap-2 font-display text-2xl text-navy">
          <Target className="h-6 w-6" /> Prospecting hub (Apollo)
        </h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Find South African employers, compliance buyers and law firms, save them to your
          pipeline, and push them straight into HubSpot for follow-up.
        </p>
      </header>

      {/* Connection banner */}
      {conn.data && (
        <div
          className={`rounded-lg border p-4 text-sm ${
            conn.data.searchEnabled
              ? "border-emerald-300 bg-emerald-50 text-emerald-900"
              : "border-amber-300 bg-amber-50 text-amber-900"
          }`}
        >
          <div className="flex items-start gap-2">
            {conn.data.searchEnabled ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <div>
              <p className="font-medium">{conn.data.message}</p>
              {conn.data.hint && <p className="mt-1">{conn.data.hint}</p>}
              {!conn.data.searchEnabled && (
                <p className="mt-1">
                  Until then you can still add leads manually below, work the pipeline and sync
                  to HubSpot.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          { label: "Leads in pipeline", value: stats.total },
          { label: "Worked / contacted", value: stats.contacted },
          { label: "Converted clients", value: stats.clients },
          { label: "Synced to HubSpot", value: stats.hubspot },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-border bg-card p-4">
            <p className="text-2xl font-semibold text-navy">{s.value}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <section className="rounded-lg border border-border bg-card p-5">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant={mode === "company" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("company")}
          >
            <Building2 className="mr-1 h-4 w-4" /> Companies
          </Button>
          <Button
            type="button"
            variant={mode === "person" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("person")}
          >
            <UserRound className="mr-1 h-4 w-4" /> Decision makers
          </Button>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {PLAYBOOKS.map((p) => (
            <Button
              key={p.label}
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  keywords: p.keywords,
                  titles: p.titles,
                  industries: p.industries,
                }))
              }
            >
              {p.label}
            </Button>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <Label htmlFor="kw">Keywords</Label>
            <Input
              id="kw"
              value={form.keywords}
              onChange={(e) => setForm({ ...form, keywords: e.target.value })}
              placeholder="e.g. logistics"
            />
          </div>
          <div>
            <Label htmlFor="loc">Locations (comma separated)</Label>
            <Input
              id="loc"
              value={form.locations}
              onChange={(e) => setForm({ ...form, locations: e.target.value })}
              placeholder="South Africa, Gauteng"
            />
          </div>
          <div>
            <Label htmlFor="ind">Industry keywords</Label>
            <Input
              id="ind"
              value={form.industries}
              onChange={(e) => setForm({ ...form, industries: e.target.value })}
              placeholder="construction, retail"
            />
          </div>
          {mode === "person" && (
            <>
              <div>
                <Label htmlFor="titles">Job titles</Label>
                <Input
                  id="titles"
                  value={form.titles}
                  onChange={(e) => setForm({ ...form, titles: e.target.value })}
                  placeholder="HR Manager, Owner"
                />
              </div>
              <div>
                <Label htmlFor="dom">Company domains</Label>
                <Input
                  id="dom"
                  value={form.domains}
                  onChange={(e) => setForm({ ...form, domains: e.target.value })}
                  placeholder="acme.co.za"
                />
              </div>
            </>
          )}
          <div>
            <Label htmlFor="emp">Employee ranges</Label>
            <Input
              id="emp"
              value={form.employeeRanges}
              onChange={(e) => setForm({ ...form, employeeRanges: e.target.value })}
              placeholder="1,10 or 11,50"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button onClick={() => search.mutate()} disabled={search.isPending}>
            {search.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Search className="mr-1 h-4 w-4" />
            )}
            Search Apollo
          </Button>
          {results.length > 0 && (
            <Button
              variant="outline"
              onClick={() => saveSelected.mutate()}
              disabled={saveSelected.isPending}
            >
              <Save className="mr-1 h-4 w-4" /> Save selected to pipeline
            </Button>
          )}
        </div>

        {searchError && (
          <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-medium">{searchError.error}</p>
            {searchError.hint && <p className="mt-1">{searchError.hint}</p>}
          </div>
        )}

        {results.length > 0 && (
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2"> </th>
                  <th className="p-2">Name</th>
                  <th className="p-2">Company</th>
                  <th className="p-2">Location</th>
                  <th className="p-2">Email</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => {
                  const key = r.apollo_id ?? String(i);
                  return (
                    <tr key={key} className="border-b border-border/60">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          aria-label={`Select ${r.name}`}
                          checked={!!picked[key]}
                          onChange={(e) => setPicked({ ...picked, [key]: e.target.checked })}
                        />
                      </td>
                      <td className="p-2">
                        <div className="font-medium text-navy">{r.name}</div>
                        {r.title && <div className="text-xs text-muted-foreground">{r.title}</div>}
                      </td>
                      <td className="p-2">{r.company ?? r.domain ?? "—"}</td>
                      <td className="p-2">{r.location ?? "—"}</td>
                      <td className="p-2">{r.email ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Manual add */}
      <section className="rounded-lg border border-border bg-card p-5">
        <h2 className="mb-3 font-display text-lg text-navy">Add a lead manually</h2>
        <div className="grid gap-3 md:grid-cols-4">
          <Input
            placeholder="Name"
            value={manual.name}
            onChange={(e) => setManual({ ...manual, name: e.target.value })}
          />
          <Input
            placeholder="Company"
            value={manual.company}
            onChange={(e) => setManual({ ...manual, company: e.target.value })}
          />
          <Input
            placeholder="Email"
            value={manual.email}
            onChange={(e) => setManual({ ...manual, email: e.target.value })}
          />
          <Input
            placeholder="Phone"
            value={manual.phone}
            onChange={(e) => setManual({ ...manual, phone: e.target.value })}
          />
        </div>
        <Button className="mt-3" variant="outline" onClick={() => addManual.mutate()}>
          <Plus className="mr-1 h-4 w-4" /> Add lead
        </Button>
      </section>

      {/* Pipeline */}
      <section className="rounded-lg border border-border bg-card p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-lg text-navy">Pipeline</h2>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!rows.length}>
            <Download className="mr-1 h-4 w-4" /> Export CSV
          </Button>
        </div>

        {leads.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !rows.length ? (
          <p className="text-sm text-muted-foreground">
            No leads yet. Run a search or add one manually.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2">Lead</th>
                  <th className="p-2">Contact</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Notes</th>
                  <th className="p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 align-top">
                    <td className="p-2">
                      <div className="font-medium text-navy">{r.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {[r.title, r.company, r.location].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </td>
                    <td className="p-2 text-xs">
                      <Input
                        className="h-8 w-48 text-xs"
                        defaultValue={r.email ?? ""}
                        placeholder="add email"
                        onBlur={(e) =>
                          e.target.value !== (r.email ?? "") &&
                          update.mutate({ id: r.id, email: e.target.value })
                        }
                      />
                      {r.phone && <div className="mt-1">{r.phone}</div>}
                      {r.synced_to_hubspot && (
                        <div className="mt-1 text-emerald-700">In HubSpot</div>
                      )}
                    </td>
                    <td className="p-2">
                      <select
                        className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                        value={r.status}
                        aria-label={`Status for ${r.name}`}
                        onChange={(e) =>
                          update.mutate({
                            id: r.id,
                            status: e.target.value,
                            markContacted: e.target.value === "contacted",
                          })
                        }
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s.replace(/_/g, " ")}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2">
                      <Textarea
                        className="min-h-[54px] w-56 text-xs"
                        defaultValue={r.notes ?? ""}
                        placeholder="Follow-up notes"
                        onBlur={(e) =>
                          e.target.value !== (r.notes ?? "") &&
                          update.mutate({ id: r.id, notes: e.target.value })
                        }
                      />
                    </td>
                    <td className="p-2">
                      <div className="flex flex-col gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toHubspot.mutate(r.id)}
                          disabled={toHubspot.isPending}
                        >
                          <Send className="mr-1 h-3 w-3" /> HubSpot
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => remove.mutate(r.id)}
                        >
                          <Trash2 className="mr-1 h-3 w-3" /> Remove
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
